import { ref, onMounted, onUnmounted, Ref } from 'vue'
import { useRouter } from 'vue-router'

/** 스와이프 비활성화 경로 */
const DISABLED_PATHS = ['/public']

/** 엣지 감지 영역 (px) */
const EDGE_THRESHOLD = 24

/** 방향 결정 최소 이동거리 (px) */
const DIRECTION_LOCK_DISTANCE = 10

/** 트리거까지 필요한 드래그 거리 (px) */
const TRIGGER_DISTANCE = 80

/** 가드 차단 감지 타임아웃 (ms) */
const GUARD_TIMEOUT = 500

type SwipeDirection = 'back' | 'forward' | null

/**
 * 현재 경로의 논리적 부모 경로 반환
 * @param path - 현재 경로
 * @returns 부모 경로 또는 null
 */
function getLogicalParentPath(path: string): string | null {
  // /task/:id/facility/:fid → /task/:id
  const facilityMatch = path.match(/^\/task\/[^/]+\/facility\/[^/]+/)
  if (facilityMatch) {
    return path.replace(/\/facility\/[^/]+.*$/, '')
  }
  // /task/:id → /
  if (/^\/task\/[^/]+/.test(path)) {
    return '/'
  }
  // /admin/* → /
  if (path.startsWith('/admin')) {
    return '/'
  }
  return null
}

/** 전역 스와이프 내비게이션 composable (뒤로/앞으로) */
export function useSwipeNavigation(): {
  swipeProgress: Ref<number>
  isSwipeActive: Ref<boolean>
  swipeDirection: Ref<SwipeDirection>
} {
  const router = useRouter()
  const swipeProgress = ref(0)
  const isSwipeActive = ref(false)
  const swipeDirection = ref<SwipeDirection>(null)

  // 논리적 back 후 forward 추적
  let logicalForwardPath: string | null = null
  // 논리적 back의 목적지 경로
  let logicalBackLandingPath: string | null = null
  let removeAfterEachHook: (() => void) | null = null

  // 해당 방향으로 네비게이션 가능 여부 확인
  function canNavigate(direction: SwipeDirection, currentPath: string): boolean {
    const state = window.history.state
    if (direction === 'back') {
      if (state?.back != null) return true
      return getLogicalParentPath(currentPath) !== null
    }
    if (direction === 'forward') {
      if (state?.forward != null) return true
      return logicalForwardPath != null && logicalBackLandingPath === currentPath
    }
    return false
  }

  let startX = 0
  let startY = 0
  let isTracking = false
  let directionLocked = false
  let currentDirection: SwipeDirection = null

  /** 현재 경로가 비활성화 대상인지 확인 */
  function isDisabledPath(): boolean {
    const path = router.currentRoute.value.path
    return DISABLED_PATHS.some(p => path.startsWith(p))
  }

  /** 온보딩 오버레이가 표시 중인지 확인 */
  function isOnboardingVisible(): boolean {
    return !!document.querySelector('.onboarding-overlay-wrapper')
  }

  /** 스와이프 시작 처리 */
  function onPointerDown(clientX: number, clientY: number): void {
    if (isDisabledPath()) return
    if (isOnboardingVisible()) return
    const isLeftEdge = clientX <= EDGE_THRESHOLD
    const isRightEdge = clientX >= window.innerWidth - EDGE_THRESHOLD
    if (!isLeftEdge && !isRightEdge) return

    const direction: SwipeDirection = isLeftEdge ? 'back' : 'forward'

    // 해당 방향으로 이동할 라우트가 없으면 무시
    if (!canNavigate(direction, router.currentRoute.value.path)) return

    startX = clientX
    startY = clientY
    isTracking = true
    directionLocked = false
    currentDirection = direction
  }

  /** 스와이프 이동 처리 */
  function onPointerMove(clientX: number, clientY: number): void {
    if (!isTracking) return

    const deltaX = clientX - startX
    const deltaY = Math.abs(clientY - startY)
    const absDeltaX = Math.abs(deltaX)

    // 방향 결정 단계
    if (!directionLocked) {
      if (absDeltaX < DIRECTION_LOCK_DISTANCE && deltaY < DIRECTION_LOCK_DISTANCE) return
      // 수직 우세이면 취소
      if (deltaY > absDeltaX) {
        cancelSwipe()
        return
      }
      // back은 오른쪽으로, forward는 왼쪽으로 드래그해야 유효
      if (currentDirection === 'back' && deltaX <= 0) {
        cancelSwipe()
        return
      }
      if (currentDirection === 'forward' && deltaX >= 0) {
        cancelSwipe()
        return
      }

      directionLocked = true
      isSwipeActive.value = true
      swipeDirection.value = currentDirection
    }

    const progress = Math.min(absDeltaX / TRIGGER_DISTANCE, 1)
    swipeProgress.value = progress
  }

  /** 스와이프 종료 처리 */
  function onPointerUp(): void {
    if (!isTracking) return
    isTracking = false

    if (!directionLocked) {
      resetState()
      return
    }

    if (swipeProgress.value >= 1) {
      triggerNavigate()
    } else {
      resetState()
    }
  }

  /** 내비게이션 실행 및 가드 차단 감지 */
  function triggerNavigate(): void {
    let navigated = false
    const dir = currentDirection

    const removeHook = router.afterEach(() => {
      navigated = true
      removeHook()
      resetState()
    })

    if (dir === 'back') {
      const hasHistoryBack = window.history.state?.back != null
      if (hasHistoryBack) {
        router.back()
      } else {
        // 히스토리 없으면 논리적 부모로 이동
        const parentPath = getLogicalParentPath(router.currentRoute.value.path)
        if (parentPath) {
          logicalForwardPath = router.currentRoute.value.fullPath
          logicalBackLandingPath = parentPath
          router.replace(parentPath)
        } else {
          removeHook()
          resetState()
          return
        }
      }
    } else {
      const hasHistoryForward = window.history.state?.forward != null
      if (hasHistoryForward) {
        router.forward()
      } else if (logicalForwardPath) {
        const target = logicalForwardPath
        logicalForwardPath = null
        logicalBackLandingPath = null
        router.push(target)
      } else {
        removeHook()
        resetState()
        return
      }
    }

    setTimeout(() => {
      removeHook()
      if (!navigated) resetState()
    }, GUARD_TIMEOUT)
  }

  /** 스와이프 취소 */
  function cancelSwipe(): void {
    isTracking = false
    directionLocked = false
    currentDirection = null
    resetState()
  }

  /** 상태 초기화 */
  function resetState(): void {
    swipeProgress.value = 0
    isSwipeActive.value = false
    swipeDirection.value = null
    currentDirection = null
  }

  // --- 이벤트 핸들러 ---
  // touchstart/touchend를 capture phase로 등록하여 d3-zoom의 stopImmediatePropagation 이전에 좌표 기록
  // touchmove는 capture phase에서 스와이프 확정 시 캔버스 전파 차단
  function handleTouchStart(e: TouchEvent): void {
    const touch = e.touches[0]
    onPointerDown(touch.clientX, touch.clientY)
  }

  function handleTouchMove(e: TouchEvent): void {
    if (!isTracking) return
    const touch = e.touches[0]
    onPointerMove(touch.clientX, touch.clientY)
    if (directionLocked) {
      e.stopPropagation()
      e.preventDefault()
    }
  }

  function handleTouchEnd(): void {
    onPointerUp()
  }

  function handleMouseDown(e: MouseEvent): void {
    if (e.button !== 0) return
    onPointerDown(e.clientX, e.clientY)
  }

  function handleMouseMove(e: MouseEvent): void {
    onPointerMove(e.clientX, e.clientY)
  }

  function handleMouseUp(): void {
    onPointerUp()
  }

  onMounted(() => {
    // 논리적 네비게이션이 아닌 일반 네비게이션 시 logicalForwardPath 초기화
    removeAfterEachHook = router.afterEach(to => {
      if (logicalForwardPath == null) return
      // landing 경로가 아닌 곳으로 이동하면 초기화
      if (to.path !== logicalBackLandingPath) {
        logicalForwardPath = null
        logicalBackLandingPath = null
      }
    })

    // capture phase로 등록하여 d3-zoom의 stopImmediatePropagation보다 먼저 실행
    document.addEventListener('touchstart', handleTouchStart, { capture: true, passive: true })
    document.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false })
    document.addEventListener('touchend', handleTouchEnd, { capture: true })
    document.addEventListener('mousedown', handleMouseDown, { capture: true })
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp, { capture: true })
  })

  onUnmounted(() => {
    removeAfterEachHook?.()
    removeAfterEachHook = null
    document.removeEventListener('touchstart', handleTouchStart, { capture: true })
    document.removeEventListener('touchmove', handleTouchMove, { capture: true })
    document.removeEventListener('touchend', handleTouchEnd, { capture: true })
    document.removeEventListener('mousedown', handleMouseDown, { capture: true })
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp, { capture: true })
  })

  return { swipeProgress, isSwipeActive, swipeDirection }
}
